import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import TopBar from "../components/TopBar";
import {
  colors,
  fonts,
  fontSizes,
  headingStyle,
  bodyTextStyle,
} from "../theme";

export default function Home() {
  const year = new Date().getFullYear();
  const [isWide, setIsWide] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => setIsWide(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div style={styles.page}>
      <TopBar />
      <div style={styles.shell}>
        <header style={styles.header}> 
          <p style={styles.intro}>
            Welcome to my corner of the internet. I’m Josh — a data analyst by trade and a builder by curiosity. I enjoy turning messy data into something useful, whether that’s dashboards, Excel and Power Query experiments, or code-driven side projects.
          </p>
          <p style={styles.intro}>
            This site is where I share work I’m proud of, explore new ideas, and learn out loud. If you’re here to browse, collaborate, or see what I’m tinkering with lately, you’re in the right place.
          </p>
        </header>

        <main style={isWide ? styles.mainWide : styles.main}>
          <section style={styles.card}>
            <h2 style={styles.h2}>Portfolio</h2>
            <p style={styles.p}>
              A curated look at analytics projects, dashboards, and experiments
              where data meets decision-making.
            </p>
            <Link style={styles.link} to="/portfolio">
              Explore projects →
            </Link>
          </section>

          <section style={styles.card}>
            <h2 style={styles.h2}>Playground</h2>
            <p style={styles.p}>
              A sandbox for ideas, experiments, and side projects — where I explore, tinker, and build beyond the boundaries of a single job title.
            </p>
            <Link style={styles.link} to="/playground">
              Explore playground →
            </Link>
          </section>

          <section style={styles.cardStatic}>
            <h2 style={styles.h2}>About</h2>
            <p style={styles.pLast}>
              Data analyst by trade, builder by curiosity. I design systems, explore data, and ship practical side projects that turn ideas into working products.
            </p>
          </section>
        </main>

        <footer style={styles.footer}>
          <div style={styles.footerContent}>
            <div style={styles.connectGroup}>
              <span style={styles.footerText}>Let's connect</span>
              <a
                href="mailto:joshtillo@gmail.com"
                style={styles.iconLink}
                aria-label="Email Josh"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/in/joshtillo/"
                target="_blank"
                rel="noreferrer"
                style={styles.iconLink}
                aria-label="Josh on LinkedIn"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                  <rect x="2" y="9" width="4" height="12" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </a>
              <span style={styles.footerDivider} aria-hidden="true">·</span>
              <Link to="/fitness-app" style={styles.footerLink}>
                Fitness App (beta)
              </Link>
              <Link to="/binder-builders" style={styles.footerLink}>
                B's Binder Builders (beta)
              </Link>
            </div>
            <div style={styles.copyright}>© {year} Josh Tillo</div>
          </div>
        </footer>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: colors.bgPrimary,
    color: colors.textPrimary,
  },

  shell: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "32px 20px 48px",
    fontFamily: fonts.body,
  },

  header: {
    marginBottom: 36,
  },

  intro: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    margin: "0 0 16px 0",
    opacity: 0.92,
    lineHeight: 1.6,
    textAlign: "left",
  },

  main: {
    background: colors.surfaceAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: 18,
    display: "grid",
    gap: 16,
  },

  mainWide: {
    background: colors.surfaceAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: 18,
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
  },

  card: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: 18,
    boxShadow: "0 8px 24px rgba(28,36,49,0.06)",
  },

  cardStatic: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: 18,
    boxShadow: "0 8px 24px rgba(28,36,49,0.04)",
  },

  h2: {
    ...headingStyle,
    fontSize: fontSizes.h2,
    fontWeight: 700,
    letterSpacing: "-0.01em",
    margin: "0 0 8px 0",
  },

  p: {
    ...bodyTextStyle,
    margin: "0 0 12px 0",
    opacity: 0.9,
  },

  pLast: {
    ...bodyTextStyle,
    margin: 0,
    opacity: 0.9,
  },

  link: {
    display: "inline-block",
    fontFamily: fonts.heading,
    fontSize: fontSizes.small,
    fontWeight: 700,
    color: colors.accentPrimary,
    textDecoration: "none",
  },

  footer: {
    marginTop: 48,
    paddingTop: 24,
    borderTop: `1px solid ${colors.border}`,
  },

  footerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },

  connectGroup: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  footerText: {
    fontFamily: fonts.heading,
    fontSize: fontSizes.small,
    fontWeight: 600,
    color: colors.textPrimary,
    opacity: 0.9,
  },

  iconLink: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: 999,
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.textPrimary,
    textDecoration: "none",
    transition: "opacity 0.2s ease",
    opacity: 0.9,
  },

  footerLink: {
    fontFamily: fonts.body,
    fontSize: fontSizes.small,
    color: colors.textPrimary,
    textDecoration: "none",
    opacity: 0.7,
    marginLeft: 4,
  },

  footerDivider: {
    opacity: 0.45,
    margin: "0 10px",
  },

  copyright: {
    fontFamily: fonts.body,
    fontSize: fontSizes.small,
    opacity: 0.7,
  },
};
